import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { APITypes } from '../../primary-record-types';
import urls from '../../utils/urls';
import useNotify from '../useNotify';
import { getIntl } from '../../utils/intl';
import { appAPI } from '../../utils/api';
import {
    getHasOrganizationLogoKey,
    getOrganizationKey,
    longTermCacheStaleTime,
    onCreateOrganization,
    onUpdateOrganization,
} from './cacheKeys';
import { useQueryErrorNotify } from '../useQueryCallbacks';
import { useExplicitAppType } from '../useAppType';
import { GetAvatarDownloadUrlResponse } from '../useAvatar';
import { OrganizationUserRole } from '../../primary-record-types/OrganizationTypes';

const intl = getIntl('useOrganization', {
    myOrganizationLoadFailure: 'Failed to load your organization',
    createOrganizationFailure: 'Failed to create organization',
    createOrganizationSuccess: 'Organization created successfully',
    updateOrganizationFailure: 'Failed to update organization',
    updateOrganizationSuccess: 'Organization updated successfully',
});

export type UseOrganizationArgs = {
    onOrganizationUpdate?: (
        updatedOrg?: APITypes.organizationService.UpdateOrganizationResponse,
        updateRequest?: APITypes.organizationService.UpdateOrganizationRequest,
    ) => void;
};

export type UseOrganizationReturnValue = {
    myOrganization: APITypes.organizationService.GetOrganizationMeResponse | null;
    isLoadingMyOrganization: boolean;
    createOrganization: (organization: APITypes.organizationService.CreateOrganizationRequest) => void;
    isCreatingOrganization: boolean;
    updateOrganization: (organization: APITypes.organizationService.UpdateOrganizationRequest) => void;
    isUpdatingOrganization: boolean;
    hasOrganizationLogo: boolean;
    isLoadingHasOrganizationLogo: boolean;
};

export default function useOrganization(args: UseOrganizationArgs): UseOrganizationReturnValue {
    const queryClient = useQueryClient();
    const notify = useNotify();
    const enableQueries = useExplicitAppType() === 'professional';

    const myOrganizationQuery = useQuery({
        queryKey: getOrganizationKey(),
        queryFn: async () => {
            return await appAPI.get<APITypes.organizationService.GetOrganizationMeResponse>(
                urls.api.organizations.myOrganization,
            );
        },
        enabled: enableQueries,
    });
    useQueryErrorNotify(myOrganizationQuery, intl('myOrganizationLoadFailure'));

    const organizationId: string | null = myOrganizationQuery.data?.organization.organizationId || null;
    const hasOrganizationLogoQuery = useQuery({
        queryKey: getHasOrganizationLogoKey(),
        queryFn: async () => {
            const result = await appAPI.get<GetAvatarDownloadUrlResponse>(urls.api.organizations.logo(organizationId!));
            return !!result;
        },
        staleTime: longTermCacheStaleTime,
        enabled: enableQueries && !!organizationId,
    });

    const createOrganization = useMutation({
        mutationFn: async (organization: APITypes.organizationService.CreateOrganizationRequest) => {
            return await appAPI.post(urls.api.organizations.createOrganization, {
                body: organization,
            });
        },
        onSuccess: () => {
            onCreateOrganization.resetCache({})();
            notify('success', intl('createOrganizationSuccess'));
            queryClient.resetQueries({ queryKey: getOrganizationKey() });
        },
        onError: () => {
            notify('error', intl('createOrganizationFailure'));
        },
    });

    const updateOrganization = useMutation({
        mutationFn: async (organization: APITypes.organizationService.UpdateOrganizationRequest) => {
            if (!myOrganizationQuery.data) throw new Error('Organization not loaded');

            return await appAPI.put(
                urls.api.organizations.organization(myOrganizationQuery.data.organization.organizationId),
                {
                    body: organization,
                },
            );
        },
        onError: () => {
            notify('error', intl('updateOrganizationFailure'));
        },
        onSuccess: (data, input) => {
            notify('success', intl('updateOrganizationSuccess'));
            onUpdateOrganization.resetCache({})();
            args.onOrganizationUpdate?.(data, input);
        },
    });

    return {
        myOrganization: myOrganizationQuery.data || null,
        isLoadingMyOrganization: myOrganizationQuery.isLoading,
        createOrganization: createOrganization.mutate,
        isCreatingOrganization: createOrganization.isPending,
        updateOrganization: updateOrganization.mutate,
        isUpdatingOrganization: updateOrganization.isPending,
        hasOrganizationLogo: hasOrganizationLogoQuery.data || false,
        isLoadingHasOrganizationLogo: hasOrganizationLogoQuery.isLoading,
    };
}

/**
 * Hook to get the organization id.
 *
 * This is a simplified hook that assumes it's being used in a professional context and in an initialized part of the app.
 *
 * If the organization is not loaded, this will throw an error.
 */
export const useOrganizationId = () => {
    const { myOrganization } = useOrganization({});
    if (!myOrganization?.organization?.organizationId) throw new Error('Organization not loaded');
    return myOrganization.organization.organizationId;
};

/**
 * Hook to get the organization role.
 *
 * This is a simplified hook that assumes it's being used in a professional context and in an initialized part of the app.
 *
 * If the organization is not loaded, this will throw an error.
 */
export const useOrganizationRole = () => {
    const { myOrganization } = useOrganization({});
    if (!myOrganization?.role) throw new Error('Organization role not loaded');
    return myOrganization.role;
};

export const useIsOrganizationAdmin = () => {
    const role = useOrganizationRole();
    return isRoleAdminOrOwner(role);
};

export const useIsOrganizationOwner = () => {
    const role = useOrganizationRole();
    return role === 'owner';
};

export const useIsOrganizationBillingAdmin = () => {
    const { myOrganization } = useOrganization({});

    if (!myOrganization) return false;

    return !!myOrganization.isBillingAdmin || myOrganization.role === 'owner';
};

export const useMaybeNotLoadedIsOrganizationOwner = () => {
    return useOrganization({})?.myOrganization?.role === 'owner';
};

export type OrganizationPaymentStatus = 'free' | 'paid' | 'loading';

export const useOrganizationPaymentStatus = () => {
    const { myOrganization } = useOrganization({});
    if (!myOrganization) return 'loading';
    return myOrganization.organization.paymentStatus;
};

export const isRoleAdminOrOwner = (role: OrganizationUserRole) => {
    return role === 'admin' || role === 'owner';
};

export const useOrganizationSessionTimeout = () => {
    const { myOrganization } = useOrganization({});
    return myOrganization?.organization.metadata.sessionTimeoutMinutes || 0;
};
